import React, {useEffect, useState} from "react";

import {Button, Popover, Tabs} from "antd";
import {useForm} from "antd/es/form/Form";
import dayjs from "dayjs";

import {
	DateRangeFilterForm,
	DATE_FORMAT,
	ExtendedDateRangeFilterForm,
	getDefaultDateRangeFields,
	getDefaultExtendedDateFields,
	isDateRangeType,
	isExtendedDateRangeType,
	validateDateRangeForm,
	validateExtendedDateRangeForm,
} from "./DateRangeFilterForm";
import {StandardForm} from "./forms/StandardForm";
import api from "../../api";

const {TabPane} = Tabs;

export const stringOptions = [
	{
		value: "full",
		label: "Full Search",
	},
	{
		value: "partial",
		label: "Partial Search",
	},
	{
		value: "not_include",
		label: "Not include",
	},
	{
		value: "startsWith",
		label: "Starts With",
	},
	{
		value: "ne",
		label: "Not Equal to",
	},
	{
		value: "in",
		label: "In List",
	},
];

export const numberOptions = [
	{
		value: "gt",
		label: "Greater than",
	},
	{
		value: "gte",
		label: "Greater than or equal to",
	},
	{
		value: "eq",
		label: "Equal to",
	},
	{
		value: "lte",
		label: "Less than or equal to",
	},
	{
		value: "lt",
		label: "Less than",
	},
	{
		value: "ne",
		label: "Not Equal to",
	},
	{
		value: "in",
		label: "In List",
	},
];

export const dateOptions = [
	{
		value: "gt",
		label: "Greater than",
	},
	{
		value: "gte",
		label: "Greater than or equal to",
	},
	{
		value: "eq",
		label: "Equal to",
	},
	{
		value: "lte",
		label: "Less than or equal to",
	},
	{
		value: "lt",
		label: "Less than",
	},
];

export const arrayOptions = [
	{
		value: "full",
		label: "Full Search",
	},
	{
		value: "partial",
		label: "Partial Search",
	},
	{
		value: "not_include",
		label: "Not include",
	},
	{
		value: "startsWith",
		label: "Starts With",
	},
	{
		value: "in",
		label: "In List",
	},
];

export const date_rangeOptions = [
	{
		value: "between",
		label: "Between",
	},
];

export const date_range_extendedOptions = [
	{
		value: "before",
		label: "Before",
	},
	{
		value: "after",
		label: "After",
	},
	{
		value: "between",
		label: "Between",
	},
];

export {getDefaultDateRangeFields, getDefaultExtendedDateFields} from "./DateRangeFilterForm";
export {isDateRangeType, isExtendedDateRangeType} from "./DateRangeFilterForm";

export const toDateOnlyString = value => {
	if (!value) {
		return null;
	}
	const date = dayjs(value);
	return date.isValid() ? date.format(DATE_FORMAT) : null;
};

export const toDateFilterValue = value => toDateOnlyString(value);

export const toDateRangeFilterValue = (valueFrom, valueTo) => {
	const from = toDateOnlyString(valueFrom);
	const to = toDateOnlyString(valueTo);
	if (!from || !to) {
		return null;
	}
	return {
		From: from,
		To: to,
	};
};

export const dateRangeFieldsFromFilterValue = value => {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		const from = value.From ?? value.from;
		const to = value.To ?? value.to;
		if (from != null && to != null) {
			return {
				valueFrom: dayjs(from),
				valueTo: dayjs(to),
			};
		}
	}
	if (Array.isArray(value) && value.length >= 2) {
		return {
			valueFrom: dayjs(value[0]),
			valueTo: dayjs(value[1]),
		};
	}
	return getDefaultDateRangeFields();
};

export const extendedDateFieldsFromFilterValue = (value, mode = "between") => {
	if (mode === "between") {
		return dateRangeFieldsFromFilterValue(value);
	}
	const dateValue = getSingleDateValue(value);
	if (dateValue != null) {
		return {value: dayjs(dateValue)};
	}
	return getDefaultExtendedDateFields(mode);
};

export const formatDateRangeValue = value => {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		const from = value.From ?? value.from;
		const to = value.To ?? value.to;
		if (from != null && to != null) {
			return `${toDateOnlyString(from)} and ${toDateOnlyString(to)}`;
		}
	}
	if (Array.isArray(value) && value.length >= 2) {
		return `${toDateOnlyString(value[0])} and ${toDateOnlyString(value[1])}`;
	}
	return value;
};

export const formatExtendedDateRangeValue = (value, mode) => {
	if (mode === "between") {
		return formatDateRangeValue(value);
	}
	const dateValue = getSingleDateValue(value);
	return toDateOnlyString(dateValue) ?? value;
};

export const toExtendedDateRangeFilterValue = (mode, values, form) => {
	if (mode === "between") {
		const from = values.valueFrom ?? form.getFieldValue("valueFrom");
		const to = values.valueTo ?? form.getFieldValue("valueTo");
		return toDateRangeFilterValue(from, to);
	}
	const value = values.value ?? form.getFieldValue("value");
	return toDateFilterValue(value);
};

export const getSingleDateValue = value => {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value.From ?? value.from ?? value.To ?? value.to ?? null;
	}
	return value ?? null;
};

export const isSameFilterValue = (a, b) => {
	if (a && b && typeof a === "object" && typeof b === "object") {
		return JSON.stringify(a) === JSON.stringify(b);
	}
	return String(a) === String(b);
};

const toFilterValue = (selected, values, attrValueForm, filterMode) => {
	if (isExtendedDateRangeType(selected.type)) {
		return toExtendedDateRangeFilterValue(filterMode, values, attrValueForm);
	}

	if (selected.type === "date_range") {
		const from = values.valueFrom ?? attrValueForm.getFieldValue("valueFrom");
		const to = values.valueTo ?? attrValueForm.getFieldValue("valueTo");
		return toDateRangeFilterValue(from, to);
	}

	if (selected.type === "date") {
		const value = values.value ?? attrValueForm.getFieldValue("value");
		return toDateFilterValue(value);
	}

	return values.value ?? attrValueForm.getFieldValue("value");
};

export function AdvancedFilter({projectId, setFilter, filters, labels = {}}) {
	const [attributes, setAttributes] = useState([]);

	const [selected, setSelected] = useState({});
	const [proposedValues, setProposedValues] = useState([]);
	const [mode, setMode] = useState("");

	const [open, setOpen] = useState(false);

	const handleOpenChange = newOpen => {
		setOpen(newOpen);
	};

	useEffect(() => {
		api.get(`/projects/${projectId}/materials/attributes`).then(json => setAttributes(json.data));
	}, []);

	const createAttributes = () =>
		attributes.map(a => ({
			type: a.type,
			value: a.field,
			label: a.label,
		}));

	const AttributesFields = [
		{
			label: "Attribute Name",
			name: "name",
			_type: "select-object",
			message: "Error",
			required: true,
			options: createAttributes(),
		},
	];

	const addFilter = values => {
		const filterMode =
			attrModeForm.getFieldValue("mode") ?? values.mode ?? mode ?? getDefaultMode();
		const value = toFilterValue(selected, values, attrValueForm, filterMode);

		setFilter([
			...filters,
			{
				key: values.name,
				value,
				mode: filterMode,
				type: selected.type,
			},
		]);
		setOpen(false);
		attrNameForm.resetFields(["name", "value"]);
		attrValueForm.resetFields(["value", "valueFrom", "valueTo"]);
	};

	const [attrNameForm] = useForm();

	const [attrValueForm] = useForm();

	const [attrModeForm] = useForm();

	const resetExtendedDateForm = nextMode => {
		attrValueForm.resetFields(["value", "valueFrom", "valueTo"]);
		attrValueForm.setFieldsValue(getDefaultExtendedDateFields(nextMode));
	};

	useEffect(() => {
		if (selected && selected.field) {
			if (selected.type === "date_range") {
				attrValueForm.setFieldsValue(getDefaultDateRangeFields());
				attrModeForm.setFieldsValue({mode: "between"});
				setMode("between");
			} else if (isExtendedDateRangeType(selected.type)) {
				const defaultMode = "between";
				resetExtendedDateForm(defaultMode);
				attrModeForm.setFieldsValue({mode: defaultMode});
				setMode(defaultMode);
			} else {
				getProposedValues();
				attrModeForm.resetFields(["mode"]);
				attrValueForm.resetFields(["value", "valueFrom", "valueTo"]);
				setMode(getDefaultMode());
			}
		} else {
			attrNameForm.resetFields(["name", "value"]);
		}
	}, [selected]);

	useEffect(() => {
		if (open) {
			setSelected({});
		}
	}, [open]);

	const onFieldsChange = fields => {
		if (fields[0].name[0] === "name") {
			setSelected(attributes.find(x => x.field === fields[0].value));
		}
	};

	const onModeFieldsChange = fields => {
		if (fields[0]?.name?.[0] === "mode" && isExtendedDateRangeType(selected.type)) {
			const nextMode = fields[0].value;
			setMode(nextMode);
			resetExtendedDateForm(nextMode);
		}
	};

	const getOptionsByType = () => {
		if (selected.type === "text") {
			return stringOptions;
		} else if (selected.type === "number") {
			return numberOptions;
		} else if (selected.type === "array") {
			return arrayOptions;
		} else if (selected.type === "date") {
			return dateOptions;
		} else if (selected.type === "date_range") {
			return date_rangeOptions;
		} else if (isExtendedDateRangeType(selected.type)) {
			return date_range_extendedOptions;
		}
		return [];
	};

	const getProposedValues = () => {
		if (isDateRangeType(selected.type)) {
			return;
		}
		api.get(`/projects/${projectId}/materials/attributes/values?attr=${selected.field}`).then(json => {
			setProposedValues(json.data);
		});
	};

	const getProposedOptions = () => proposedValues;

	const getModeValue = () => [
		{
			label: "",
			name: "mode",
			_type: "select-object",
			message: "Error",
			required: true,
			options: getOptionsByType(),
			props: {allowClear: false},
		},
	];

	const getValuesFields = () => {
		if (selected.type === "date") {
			return [
				{
					label: "Attribute Value",
					name: "value",
					_type: "date",
				},
			];
		}
		return [{label: "Attribute Value", name: "value", _type: "autocomplete", options: getProposedOptions()}];
	};

	const addFilterFull = async () => {
		const filterMode = attrModeForm.getFieldValue("mode") ?? mode ?? getDefaultMode();

		if (isExtendedDateRangeType(selected.type)) {
			try {
				await validateExtendedDateRangeForm(attrValueForm, filterMode);
			} catch {
				return;
			}
		} else if (selected.type === "date_range") {
			try {
				await validateDateRangeForm(attrValueForm);
			} catch {
				return;
			}
		}

		const result = {
			name: attrNameForm.getFieldValue("name"),
			value: attrValueForm.getFieldValue("value"),
			valueFrom: attrValueForm.getFieldValue("valueFrom"),
			valueTo: attrValueForm.getFieldValue("valueTo"),
			mode: filterMode,
			type: selected.type,
		};
		addFilter(result);
	};

	const getDefaultMode = () => {
		if (selected.type === "text") {
			return "full";
		} else if (selected.type === "number") {
			return "eq";
		} else if (selected.type === "array") {
			return "full";
		} else if (selected.type === "date") {
			return "eq";
		} else if (selected.type === "date_range") {
			return "between";
		} else if (isExtendedDateRangeType(selected.type)) {
			return "between";
		}
		return "";
	};

	const showModeForm = selected?.field && getOptionsByType().length > 0;
	const currentMode = mode || getDefaultMode();

	const content = (
		<div style={{width: 350}}>
			<Tabs defaultActiveKey={"1"}>
				<TabPane tab={"Attribute"} key={"1"}>
					<StandardForm
						onFieldsChange={onFieldsChange}
						form={attrNameForm}
						fields={AttributesFields}
						onFinish={addFilter}
					/>

					{selected && selected.field && selected.type === "date_range" && (
						<DateRangeFilterForm
							form={attrValueForm}
							initialValues={getDefaultDateRangeFields()}
							onFinish={addFilterFull}
						/>
					)}

					{selected && selected.field && isExtendedDateRangeType(selected.type) && (
						<ExtendedDateRangeFilterForm
							key={currentMode}
							form={attrValueForm}
							mode={currentMode}
							initialValues={getDefaultExtendedDateFields(currentMode)}
							onFinish={addFilterFull}
						/>
					)}

					{selected && selected.field && !isDateRangeType(selected.type) && (
						<StandardForm
							form={attrValueForm}
							fields={getValuesFields()}
							onFinish={addFilterFull}
						/>
					)}

					{showModeForm && (
						<StandardForm
							form={attrModeForm}
							initialValues={{mode: getDefaultMode()}}
							fields={getModeValue()}
							onFieldsChange={onModeFieldsChange}
							onFinish={() => {}}
						/>
					)}

					<Button className={"submit-button"} style={{float: "right"}} onClick={() => addFilterFull()}>
						Submit
					</Button>
				</TabPane>
			</Tabs>
		</div>
	);

	return (
		<Popover
			open={open}
			onOpenChange={handleOpenChange}
			placement="bottom"
			content={content}
			title="Advanced Filter"
			trigger="click">
			<Button
				onClick={() => setOpen(true)}
				size="small"
				icon={<img src={process.env.PUBLIC_URL + "/images/filter_list-24px.svg"} />}>
				Add Filter
			</Button>
		</Popover>
	);
}
